import { supabase } from '@/lib/supabase';

interface PathPrerequisite {
  pathId: string;
  requiredProgress: number;
}

interface ValidationResult {
  isValid: boolean;
  missingPrerequisites: {
    pathId: string;
    title: string;
    currentProgress: number;
    requiredProgress: number;
  }[];
}

export async function validatePathPrerequisites(
  userId: string,
  pathId: string
): Promise<ValidationResult> {
  try {
    const { data: prerequisites, error: prereqError } = await supabase
      .from('learning_path_prerequisites')
      .select(`
        required_path_id,
        required_progress,
        required_path:learning_paths(title)
      `)
      .eq('path_id', pathId);

    if (prereqError) throw prereqError;

    if (!prerequisites?.length) {
      return { isValid: true, missingPrerequisites: [] };
    }

    const { data: progress, error: progressError } = await supabase
      .from('learning_progress')
      .select('path_id, progress')
      .eq('user_id', userId)
      .in(
        'path_id', 
        prerequisites.map(p => p.required_path_id)
      );

    if (progressError) throw progressError;

    const missingPrerequisites = prerequisites.reduce((missing, prereq) => {
      const userProgress = progress?.find(p => p.path_id === prereq.required_path_id);
      const currentProgress = userProgress?.progress || 0;

      if (currentProgress < prereq.required_progress) {
        missing.push({
          pathId: prereq.required_path_id,
          title: prereq.required_path.title,
          currentProgress,
          requiredProgress: prereq.required_progress
        });
      }

      return missing;
    }, [] as ValidationResult['missingPrerequisites']);

    return {
      isValid: missingPrerequisites.length === 0,
      missingPrerequisites
    };
  } catch (error) {
    console.error('Error validating prerequisites:', error);
    throw error;
  }
}

// Helper function to format prerequisite messages
export function formatPrerequisiteMessage(
  missingPrerequisites: ValidationResult['missingPrerequisites']
): string {
  if (!missingPrerequisites.length) return '';

  const prerequisites = missingPrerequisites.map(prereq => (
    `Complete ${prereq.requiredProgress}% of "${prereq.title}" (current: ${prereq.currentProgress}%)`
  ));

  return `Required before starting:\n${prerequisites.join('\n')}`;
} 